/**
 * Team Integration Configuration
 * 
 * Each team can define their own E2E integration pipelines.
 * Only teams with defined integrations will show the "Integrations" tab.
 * 
 * Structure:
 * - teamName: matches the internal team name from teamConfig.js
 * - environments: which environments have integrations (e.g., ['prod'] or ['dev', 'prod'])
 * - pipelines: array of pipeline definitions
 */

export const teamIntegrations = {
  // IES Team Integrations
  IES: {
    displayName: 'IES Integrations',
    description: 'IES data flow monitoring',
    environments: ['prod'], // Only show for IES prod
    pipelines: [
      {
        id: 'irma-dml-ies',
        name: 'IRMA → DML → IES',
        description: 'IRMA integration data flow',
        schedule: 'Daily',
        stages: [
          {
            id: 'irma',
            name: 'IRMA',
            icon: '🏢',
            sourceType: 'external', // TBD - need to define how to get count
            description: 'Source system (count method TBD)',
            config: {
              // TODO: Add IRMA API config or CloudWatch metric
            }
          },
          {
            id: 'dml-s3',
            name: 'DML S3',
            icon: '🪣',
            sourceType: 's3_object_count',
            description: 'S3 bucket object count',
            config: {
              bucket: '', // TODO: Add DML S3 bucket name
              prefix: 'irma-data/',
              accountId: '', // TODO: Add DML account ID
              region: 'eu-west-1'
            }
          },
          {
            id: 'ies-s3',
            name: 'IES S3',
            icon: '🪣',
            sourceType: 's3_object_count',
            description: 'S3 bucket file count',
            config: {
              bucket: 'ies-app-prod-irma-dml-data',
              accountId: '279110638550',
              region: 'eu-central-1'
            }
          }
        ]
      },
      {
        id: 'workday-dml-ies',
        name: 'Workday → DML → IES',
        description: 'Employee data from Workday (syncs every 4 hours)',
        schedule: 'Every 4 hours',
        stages: [
          {
            id: 'workday',
            name: 'Workday',
            icon: '👥',
            sourceType: 'external', // TBD - need Workday API or metrics
            description: 'Employee data source (count method TBD)',
            config: {
              // TODO: Add Workday API config
            }
          },
          {
            id: 'dml-s3-workday',
            name: 'DML S3',
            icon: '🪣',
            sourceType: 's3_object_count',
            description: 'S3 bucket object count',
            config: {
              bucket: '', // TODO: Add DML employee bucket name
              prefix: 'employee-data/',
              accountId: '', // TODO: Add DML account ID
              region: 'eu-central-1' // IES uses Frankfurt
            }
          },
          {
            id: 'ies-dynamodb-employee',
            name: 'IES DynamoDB',
            icon: '🗄️',
            sourceType: 'dynamodb_count',
            description: 'Employee table - ies-app-prod-employee-persistent-employees',
            config: {
              tableName: 'ies-app-prod-employee-persistent-employees',
              accountId: '279110638550', // IES Prod
              region: 'eu-central-1',
              timestampField: 'modifiedAt' // Field to filter by date/time
            }
          }
        ]
      }
    ]
  }
  
  // Add more teams as needed:
  // SMP: { ... },
  // MyKONE: { ... },
};

/**
 * Vulnerability Dashboard Configuration
 * 
 * Defines which teams have access to the vulnerability dashboard
 * and Zafran AI remediation features.
 * 
 * TEAM-LEVEL VULNERABILITY DASHBOARD:
 * - Dashboard is shown ONLY in PROD environment for each team
 * - When viewing PROD, shows vulnerabilities from ALL environments (dev, qa, prod)
 * - Teams must be in enabledTeams to see the vulnerability tab
 */
export const vulnerabilityConfig = {
  enabled: true,
  features: {
    viewVulnerabilities: true,
    triggerRemediation: true,
    viewStats: true,
    exportReports: true
  },
  severityColors: {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#ca8a04',
    low: '#16a34a',
    info: '#6b7280'
  },
  autoRefreshInterval: 300000, // 5 minutes
  
  // Teams with vulnerability dashboard access
  // Dashboard shows ONLY in PROD, but displays vulnerabilities from ALL environments
  // Data is fetched from S3 (collected daily by scheduled Lambda)
  enabledTeams: ['iCOP', 'IES', 'SMP', 'IRMA', 'MyKONE', 'LogBook'],
  
  // Environment where dashboard is visible (typically 'prod' only)
  dashboardEnvironment: 'prod'
};

/**
 * Team-specific Zafran configuration
 * Mirrors backend/teams/zafran/team_zafran_config.py
 */
export const teamZafranConfig = {
  'IES': {
    enabled: true,
    displayName: 'IES Security',
    description: 'IES team vulnerability management',
    dashboardEnvironment: 'prod',
    // Monitored environments - vulns from all these shown in PROD dashboard
    accounts: {
      'dev': '432520554350',
      'qa': '226078374497',
      'prod': '279110638550'
    }
  },
  'SMP': {
    enabled: true,
    displayName: 'SMP Security',
    description: 'SMP platform vulnerability management',
    dashboardEnvironment: 'prod',
    accounts: {
      'dev': '577638395954',
      'acc': '108782074958',
      'prod': '779846812953'
    }
  },
  'IRMA': {
    enabled: true,
    displayName: 'IRMA Security',
    description: 'IRMA services vulnerability management',
    dashboardEnvironment: 'prod',
    accounts: {
      'dev': '006805626247',
      'qa': '860655786540',
      'prod': '797776192795'
    }
  },
  'MyKONE': {
    enabled: true,
    displayName: 'MyKONE Security',
    description: 'MyKONE Delivery Ops vulnerability management',
    dashboardEnvironment: 'prod',
    accounts: {
      'dev': '038462775582',
      'prod': '183295439344'
    }
  },
  'LogBook': {
    enabled: true,
    displayName: 'LogBook Security',
    description: 'LogBook platform vulnerability management',
    dashboardEnvironment: 'prod',
    accounts: {
      'dev': '508152319109',
      'prod': '082095646959'
    }
  },
  'iCOP': {
    enabled: true,  // Data fetched from S3 (collected daily)
    displayName: 'iCOP Security',
    description: 'iCOP platform vulnerability management',
    dashboardEnvironment: 'prod',
    accounts: {
      'dev': '214888068672',
      'prod': '355878730084'
    }
  }
};

/**
 * Check if vulnerability dashboard is enabled for a team
 * @param {string} teamName - Internal team name
 * @returns {boolean}
 */
export const isVulnerabilityDashboardEnabled = (teamName) => {
  return vulnerabilityConfig.enabled && 
         vulnerabilityConfig.enabledTeams.includes(teamName);
};

/**
 * Check if vulnerability dashboard should be shown for team+environment
 * Dashboard shows ONLY in PROD (or configured dashboardEnvironment)
 * @param {string} teamName - Internal team name
 * @param {string} environment - Current environment being viewed
 * @returns {boolean}
 */
export const shouldShowVulnerabilityTab = (teamName, environment) => {
  if (!isVulnerabilityDashboardEnabled(teamName)) return false;
  
  const teamConfig = teamZafranConfig[teamName];
  if (!teamConfig || !teamConfig.enabled) return false;
  
  // Show only in the dashboard environment (typically 'prod')
  const dashboardEnv = teamConfig.dashboardEnvironment || 'prod';
  return environment.toLowerCase() === dashboardEnv.toLowerCase();
};

/**
 * JIRA (Zafran remediation tickets) - applications that have a JIRA board
 * configured. This MUST stay in sync with the backend
 * (backend/teams/jira/jira_config.py -> enabled applications with a parent key).
 *
 * The keys here are INTERNAL team/application names (e.g. 'MyKONE'), which is
 * also the S3 folder + the API path segment: jira/applications/{app}.
 */
// All internal teams: the feature is now self-serve (each team configures its
// own EPIC / repositories / GitHub token from the "Manage repositories &
// config" panel in the section). Teams without config yet simply see an empty
// ticket list plus the config panel; the backend collector only pulls tickets
// for apps that have an effective EPIC (UI-set or hardcoded), so enabling a
// team here never produces spurious data.
export const jiraEnabledApps = [
  'iCOP', 'MyKONE', 'IES', 'SMP', 'LogBook',
  'IRMA', 'MyKONE-PSR', 'MyKONE-Platform', 'MyKONE-CIAM', 'KONE-Support',
  'DMI',
];

/**
 * Environment in which the JIRA remediation-tickets section is visible.
 * The section (like the vulnerability dashboard) is PROD-only.
 */
export const jiraDashboardEnvironment = 'prod';

/**
 * Per-team overrides for the environment(s) in which the JIRA section is
 * visible. A team listed here sees the section in the given environment(s) in
 * ADDITION to the default prod-only rule; teams not listed stay prod-only.
 * DMI runs only in dev, so its remediation tickets are surfaced there.
 */
export const jiraDashboardEnvironmentOverrides = {
  DMI: ['dev'],
};

/**
 * Whether the JIRA remediation-tickets section should render for a team.
 * The section is only shown in PROD. When an environment is provided it is
 * validated against jiraDashboardEnvironment; when omitted (legacy callers)
 * only the app-enablement check is applied.
 * @param {string} internalTeamName - Internal team name (e.g. 'MyKONE')
 * @param {string} [environment] - Current environment being viewed (e.g. 'dev', 'prod')
 * @returns {boolean}
 */
export const isJiraEnabled = (internalTeamName, environment) => {
  if (!jiraEnabledApps.includes(internalTeamName)) return false;
  if (environment == null) return true;
  const env = environment.toLowerCase();
  if (env === jiraDashboardEnvironment.toLowerCase()) return true;
  const overrides = jiraDashboardEnvironmentOverrides[internalTeamName] || [];
  return overrides.some((e) => e.toLowerCase() === env);
};

/**
 * Get all monitored environments for a team
 * Returns all environments whose vulnerabilities are shown in the dashboard
 * @param {string} teamName - Internal team name
 * @returns {string[]}
 */
export const getTeamMonitoredEnvironments = (teamName) => {
  const teamConfig = teamZafranConfig[teamName];
  if (!teamConfig || !teamConfig.accounts) return [];
  return Object.keys(teamConfig.accounts);
};

/**
 * Get Zafran configuration for a team
 * @param {string} teamName - Internal team name
 * @returns {object|null}
 */
export const getTeamZafranConfig = (teamName) => {
  return teamZafranConfig[teamName] || null;
};

/**
 * Check if a team has integrations configured for a specific environment
 * @param {string} teamName - Internal team name (e.g., 'IES', 'SMP')
 * @param {string} environment - Environment (e.g., 'dev', 'prod')
 * @returns {boolean}
 */
export const hasTeamIntegrations = (teamName, environment) => {
  const teamConfig = teamIntegrations[teamName];
  if (!teamConfig) return false;
  
  // Check if the team has integrations for this environment
  return teamConfig.environments.includes(environment.toLowerCase());
};

/**
 * Get integration configuration for a team and environment
 * @param {string} teamName - Internal team name
 * @param {string} environment - Environment
 * @returns {object|null}
 */
export const getTeamIntegrations = (teamName, environment) => {
  if (!hasTeamIntegrations(teamName, environment)) return null;
  return teamIntegrations[teamName];
};

/**
 * Get all teams that have integrations configured
 * @returns {string[]}
 */
export const getTeamsWithIntegrations = () => {
  return Object.keys(teamIntegrations);
};

export default teamIntegrations;
